import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { RocketLaunch } from "@mui/icons-material";

import { API_URL } from "../helpers/constants";
import { getToken } from "../helpers/tokenStorage";
import {
  brandButton,
  darkSurface,
  heroText,
  motionVariants,
} from "../helpers/designSystem";

/**
 * First-login workspace naming prompt.
 *
 * Fires on app mount when the user has any project with
 * needsNaming=true — i.e. they were just provisioned via the
 * invitation-redemption flow and haven't picked a name for their
 * default workspace yet. Modal pre-fills the placeholder name and
 * lets them accept or rename. After submit, the project is renamed
 * server-side and the flag is cleared permanently (modal never
 * fires for that project again).
 *
 * Designed to feel like a continuation of the redemption ceremony,
 * not an interruption. Once dismissed it shouldn't fire again for
 * that session even if the network call is slow.
 */
export default function WelcomeNamingModal() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [candidate, setCandidate] = useState(null); // {id, name}
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const token = getToken();

  // On mount (or token change), look for any project flagged
  // needsNaming. Only authenticated users can hit this endpoint, so
  // bail silently when there's no token.
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/k8s-project/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((list) => {
        const pending = Array.isArray(list)
          ? list.find((p) => p && p.needsNaming === true)
          : null;
        if (pending) {
          setCandidate({ id: pending.id, name: pending.name });
          setValue(pending.name || "");
        }
      })
      .catch(() => {
        // Don't block first login on a project-list error.
      });
  }, [token]);

  const handleSave = () => {
    if (!candidate || !value.trim()) return;
    setSubmitting(true);
    setError(null);
    fetch(`${API_URL}/k8s-project/${candidate.id}/onboard-name`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name: value.trim() }),
    })
      .then((r) =>
        r.ok
          ? r.json()
          : r.text().then((t) => Promise.reject(new Error(t || `HTTP ${r.status}`)))
      )
      .then(() => {
        setCandidate(null);
        setValue("");
        // Light reload so the project picker + apps list refresh
        // with the new project name everywhere.
        window.location.reload();
      })
      .catch((err) => setError(err.message || "Could not save the name. Try again."))
      .finally(() => setSubmitting(false));
  };

  if (!candidate) return null;

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      // Block dismissal — user picks a name or accepts the default.
      disableEscapeKeyDown
      onClose={() => {}}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          background: isDarkMode
            ? "radial-gradient(ellipse at 20% 0%, rgba(234,88,12,0.15), transparent 50%), " +
              `linear-gradient(180deg, ${darkSurface.base} 0%, ${darkSurface.raised} 100%)`
            : "linear-gradient(180deg, #ffffff 0%, #fff7ed 100%)",
          color: isDarkMode ? darkSurface.text : "#0f172a",
          boxShadow: "0 24px 64px rgba(15,23,42,0.4), 0 0 0 1px rgba(234,88,12,0.15)",
        },
      }}
    >
      <Box
        component={motion.div}
        initial="initial"
        animate="animate"
        variants={motionVariants.heroEnter}
        sx={{ p: { xs: 3, sm: 4 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              background: "linear-gradient(135deg, #ea580c 0%, #f97316 60%, #fb923c 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 18px rgba(234,88,12,0.45)",
            }}
          >
            <RocketLaunch sx={{ color: "#fff" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              ...heroText(isDarkMode),
              fontSize: { xs: "1.4rem", sm: "1.7rem" },
            }}
          >
            Your Clue2App account is ready
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? darkSurface.textMuted : "#475569",
            mb: 1,
          }}
        >
          You're the <strong>admin</strong> of this account — you can add
          more workspaces, invite teammates, and manage billing later from
          account settings.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: isDarkMode ? darkSurface.textMuted : "#64748b",
            mb: 3,
          }}
        >
          To get started, pick a name for your first workspace.
        </Typography>

        <TextField
          autoFocus
          fullWidth
          label="Workspace name"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !submitting && value.trim()) {
              handleSave();
            }
          }}
          inputProps={{ maxLength: 80 }}
          sx={{
            mb: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
        {error && (
          <Typography variant="caption" sx={{ color: "#ef4444", display: "block", mb: 1 }}>
            {error}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            color: isDarkMode ? darkSurface.textFaint : "#94a3b8",
            display: "block",
            mb: 3,
          }}
        >
          A workspace holds your apps, environment variables, and
          custom domains.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            disabled={submitting || !value.trim()}
            onClick={handleSave}
            sx={{ ...brandButton(), height: 44, px: 3 }}
          >
            {submitting ? "Saving…" : "Save and continue"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
